import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Leaf, Sparkles, ChevronLeft, ChevronRight, Star, Heart, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const LoungePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const loungeImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/uqsrp4eo_IMG_1070.jpeg',
      title: 'The Amlaria Lounge',
      subtitle: 'Where Tradition Meets Luxury'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_ce00ca01-0951-4822-8b72-1668a488d246/artifacts/93wmkx6j_IMG_1071.jpeg',
      title: 'Elegant Comfort',
      subtitle: 'Premium Design'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % loungeImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [loungeImages.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % loungeImages.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + loungeImages.length) % loungeImages.length);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="lounge-page">
      {/* Hero Section with Bohemian Lounge Images */}
      <section className="relative h-[90vh] overflow-hidden" data-testid="lounge-hero">
        {/* Image Slideshow */}
        <div className="absolute inset-0">
          {loungeImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
            </div>
          ))}
        </div>

        {/* 3D Geometric Pattern Side Panels - Kilim Inspired */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10">
          <div className="h-full w-full bg-stone-100/90 backdrop-blur-sm" 
            style={{
              backgroundImage: `
                repeating-linear-gradient(0deg, transparent, transparent 15px, #78350f20 15px, #78350f20 17px),
                repeating-linear-gradient(90deg, transparent, transparent 15px, #78350f10 15px, #78350f10 17px)
              `
            }}
          >
            {/* Kilim-inspired stepped pattern */}
            <svg className="w-full h-full opacity-30" preserveAspectRatio="none" viewBox="0 0 100 1000">
              <defs>
                <pattern id="kilimLeft" patternUnits="userSpaceOnUse" width="100" height="200">
                  <path d="M0,100 L50,50 L100,100 L50,150 Z" fill="#78350f" opacity="0.3"/>
                  <path d="M0,0 L50,50 L0,100 Z" fill="#dc2626" opacity="0.2"/>
                  <path d="M100,0 L50,50 L100,100 Z" fill="#f59e0b" opacity="0.2"/>
                </pattern>
              </defs>
              <rect width="100" height="1000" fill="url(#kilimLeft)"/>
            </svg>
          </div>
        </div>
        
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10">
          <div className="h-full w-full bg-stone-100/90 backdrop-blur-sm"
            style={{
              backgroundImage: `
                repeating-linear-gradient(0deg, transparent, transparent 15px, #78350f20 15px, #78350f20 17px),
                repeating-linear-gradient(90deg, transparent, transparent 15px, #78350f10 15px, #78350f10 17px)
              `
            }}
          >
            <svg className="w-full h-full opacity-30" preserveAspectRatio="none" viewBox="0 0 100 1000">
              <defs>
                <pattern id="kilimRight" patternUnits="userSpaceOnUse" width="100" height="200">
                  <path d="M0,100 L50,50 L100,100 L50,150 Z" fill="#78350f" opacity="0.3"/>
                  <path d="M0,0 L50,50 L0,100 Z" fill="#f59e0b" opacity="0.2"/>
                  <path d="M100,0 L50,50 L100,100 Z" fill="#dc2626" opacity="0.2"/>
                </pattern>
              </defs>
              <rect width="100" height="1000" fill="url(#kilimRight)"/>
            </svg>
          </div>
        </div>

        {/* Golden Rod Ceiling Detail */}
        <div className="absolute top-0 left-16 md:left-24 right-16 md:right-24 h-12 z-20">
          <div className="h-full w-full bg-gradient-to-b from-amber-600/80 to-amber-700/60"
            style={{
              backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 2px, #fcd34d 2px, #fcd34d 4px, transparent 4px, transparent 8px)`
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex items-center h-full px-8 md:px-16 lg:px-24">
          <div className="max-w-2xl ml-8 md:ml-16">
            {/* Decorative Element */}
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-amber-400"></div>
              <Sparkles className="h-5 w-5 text-amber-400" />
              <span className="text-amber-300 text-sm tracking-[0.3em] uppercase font-light">Welcome to</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-4 tracking-tight leading-none">
              {loungeImages[currentSlide].title}
            </h1>
            
            <p className="text-xl md:text-2xl text-amber-100 mb-8 font-light tracking-wide">
              {loungeImages[currentSlide].subtitle}
            </p>

            <p className="text-stone-200 text-lg mb-10 max-w-lg leading-relaxed">
              Experience the warmth of traditional craftsmanship in our exclusive showroom. 
              Where every detail tells a story of premium excellence and timeless beauty.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/shop/iphone-cases">
                <Button 
                  size="lg" 
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg font-semibold rounded-none shadow-xl hover:shadow-2xl transition-all border border-amber-500"
                  data-testid="explore-collection-btn"
                >
                  Explore Collection
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                className="bg-transparent border-2 border-stone-200 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-none backdrop-blur-sm"
                data-testid="book-visit-btn"
              >
                Book a Visit
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-28 md:left-36 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all text-white rounded-full"
          data-testid="prev-slide-btn"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-28 md:right-36 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all text-white rounded-full"
          data-testid="next-slide-btn"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {loungeImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-12 h-1.5 rounded-full transition-all ${
                index === currentSlide ? 'bg-amber-400' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Bohemian Pattern Divider */}
      <div className="h-4 w-full bg-gradient-to-r from-amber-700 via-red-700 to-amber-700"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, 
            #b45309 0px, #b45309 30px, 
            #dc2626 30px, #dc2626 60px, 
            #78350f 60px, #78350f 90px,
            #ea580c 90px, #ea580c 120px)`
        }}
      ></div>

      {/* About the Lounge Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="lounge-about">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image Composition */}
            <div className="relative">
              {/* Main Image */}
              <div className="relative overflow-hidden shadow-2xl">
                <img
                  src={loungeImages[0].url}
                  alt="Amlaria Lounge Interior"
                  className="w-full h-[500px] object-cover"
                />
                {/* Kilim Pattern Frame */}
                <div className="absolute inset-0 border-8 border-stone-800/10 pointer-events-none"
                  style={{
                    backgroundImage: `linear-gradient(to right, rgba(120,53,15,0.1) 0%, transparent 5%, transparent 95%, rgba(120,53,15,0.1) 100%)`
                  }}
                ></div>
              </div>
              
              {/* Floating Accent Image */}
              <div className="absolute -bottom-8 -right-8 w-48 h-48 overflow-hidden shadow-xl border-4 border-white hidden md:block">
                <img
                  src={loungeImages[1].url}
                  alt="Lounge Detail"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-4 border-amber-600/30"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-red-600/20 hidden md:block" style={{ transform: 'translateX(60px)' }}></div>
            </div>

            {/* Right - Content */}
            <div className="lg:pl-8">
              <span className="inline-block bg-amber-100 text-amber-800 px-4 py-2 text-sm font-semibold tracking-wider uppercase mb-6">
                Our Showroom
              </span>
              
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-6 leading-tight">
                A Haven of
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-700 to-red-700">
                  Bohemian Beauty
                </span>
              </h2>

              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                Step into the Amlaria Lounge, where the rich heritage of traditional kilim craftsmanship 
                meets contemporary luxury. Our showroom is more than a space—it's an experience that 
                celebrates the artistry behind every piece we create.
              </p>

              <p className="text-stone-600 mb-10 leading-relaxed">
                Surrounded by handwoven textiles, warm wooden accents, and the gentle glow of golden 
                light, discover our curated collection of premium phone cases and accessories. Each 
                visit is an invitation to explore the intersection of protection and artistry.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 flex items-center justify-center">
                    <Star className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">Premium Quality</p>
                    <p className="text-sm text-stone-500">Handpicked materials</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 flex items-center justify-center">
                    <Heart className="h-5 w-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">Made with Love</p>
                    <p className="text-sm text-stone-500">Premium crafted</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">Lifetime Warranty</p>
                    <p className="text-sm text-stone-500">We stand behind our work</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
                    <Leaf className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-900">Eco-Friendly</p>
                    <p className="text-sm text-stone-500">Sustainable practices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kilim-Inspired Gallery Section */}
      <section className="py-20 bg-stone-900 relative overflow-hidden" data-testid="lounge-gallery">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 40px, #fbbf24 40px, #fbbf24 42px),
              repeating-linear-gradient(-45deg, transparent, transparent 40px, #dc2626 40px, #dc2626 42px)
            `
          }}
        ></div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase font-light">Gallery</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 mb-6">
              The Amlaria <span className="text-amber-400">Experience</span>
            </h2>
            <p className="text-stone-400 text-lg max-w-2xl mx-auto">
              Explore the beauty of our showroom through these carefully captured moments.
            </p>
          </div>

          {/* Image Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group relative overflow-hidden">
              <img
                src={loungeImages[0].url}
                alt="Lounge View 1"
                className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-amber-400 text-sm tracking-wider uppercase mb-1">Lounge</p>
                  <h3 className="text-2xl font-bold">The Main Hall</h3>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden">
              <img
                src={loungeImages[1].url}
                alt="Lounge View 2"
                className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-amber-400 text-sm tracking-wider uppercase mb-1">Comfort</p>
                  <h3 className="text-2xl font-bold">Seating Area</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Amenities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-amber-50" data-testid="lounge-services">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <span className="text-amber-700 text-sm tracking-[0.3em] uppercase font-light">What Awaits</span>
            <h2 className="text-4xl md:text-5xl font-black text-stone-900 mt-4 mb-6">
              Lounge <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-700 to-red-700">Services</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white p-8 shadow-lg hover:shadow-xl transition-shadow group">
              <div className="w-16 h-16 bg-amber-100 flex items-center justify-center mb-6 group-hover:bg-amber-200 transition-colors">
                <Award className="h-8 w-8 text-amber-700" />
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Personal Consultation</h3>
              <p className="text-stone-600 leading-relaxed">
                Our experts guide you through our collection, helping you find the perfect case that 
                matches your style and device. Experience one-on-one attention in a relaxed setting.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white p-8 shadow-lg hover:shadow-xl transition-shadow group">
              <div className="w-16 h-16 bg-red-100 flex items-center justify-center mb-6 group-hover:bg-red-200 transition-colors">
                <Sparkles className="h-8 w-8 text-red-700" />
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Custom Designs</h3>
              <p className="text-stone-600 leading-relaxed">
                Work with our designers to create a unique, personalized phone case. Choose patterns, 
                colors, and materials that reflect your individual taste.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white p-8 shadow-lg hover:shadow-xl transition-shadow group">
              <div className="w-16 h-16 bg-orange-100 flex items-center justify-center mb-6 group-hover:bg-orange-200 transition-colors">
                <Star className="h-8 w-8 text-orange-700" />
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Complimentary Beverages</h3>
              <p className="text-stone-600 leading-relaxed">
                Enjoy premium coffee or traditional tea while you browse. Our lounge is designed for 
                comfort and relaxation—take your time and savor the experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" 
        style={{
          background: 'linear-gradient(135deg, #78350f 0%, #dc2626 50%, #ea580c 100%)'
        }}
        data-testid="lounge-stats"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-5xl md:text-6xl font-black mb-2">5000+</p>
              <p className="text-amber-200 text-lg">Happy Customers</p>
            </div>
            <div>
              <p className="text-5xl md:text-6xl font-black mb-2">100%</p>
              <p className="text-amber-200 text-lg">Quality Assured</p>
            </div>
            <div>
              <p className="text-5xl md:text-6xl font-black mb-2">24/7</p>
              <p className="text-amber-200 text-lg">Customer Support</p>
            </div>
            <div>
              <p className="text-5xl md:text-6xl font-black mb-2">4.9</p>
              <p className="text-amber-200 text-lg">Star Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Visit Information */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="lounge-visit-info">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Contact Info */}
            <div>
              <span className="text-amber-700 text-sm tracking-[0.3em] uppercase font-light">Plan Your Visit</span>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mt-4 mb-8">
                Find <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-700 to-red-700">Us</span>
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-lg mb-1">Location</h4>
                    <p className="text-stone-600">123 Fashion Avenue, Design District<br/>New York, NY 10001</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-red-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-lg mb-1">Opening Hours</h4>
                    <p className="text-stone-600">Monday - Saturday: 10:00 AM - 8:00 PM<br/>Sunday: 12:00 PM - 6:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-orange-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-lg mb-1">Phone</h4>
                    <p className="text-stone-600">+1 (555) 123-4567</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900 text-lg mb-1">Email</h4>
                    <p className="text-stone-600">lounge@amlaria.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Map Placeholder / Image */}
            <div className="relative">
              <div className="bg-stone-100 h-full min-h-[400px] overflow-hidden">
                <img
                  src={loungeImages[0].url}
                  alt="Amlaria Lounge Entrance"
                  className="w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <p className="text-amber-300 text-sm uppercase tracking-wider mb-2">Welcome to</p>
                  <h3 className="text-3xl font-bold mb-4">The Amlaria Lounge</h3>
                  <Link to="/shop/iphone-cases">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-none" data-testid="visit-shop-btn">
                      Visit Our Shop
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-stone-900" data-testid="lounge-cta">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="h-10 w-10 text-amber-400 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Ready to Experience<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-red-400">Amlaria?</span>
          </h2>
          <p className="text-xl text-stone-400 mb-10 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have discovered the perfect blend 
            of protection and artistry. Visit us today or explore our collection online.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/shop/iphone-cases">
              <Button 
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg font-semibold rounded-none"
                data-testid="shop-now-btn"
              >
                Shop Collection
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-stone-600 text-stone-300 hover:bg-stone-800 px-10 py-6 text-lg font-semibold rounded-none"
              data-testid="contact-us-btn"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoungePage;
